/**
 * カード — 軍事（ぐんじ）20枚
 *
 * docs/01-ゲームデザイン.md §9 の「軍事 20枚」に対応する。
 *
 * 【設計方針】
 * 合戦は docs/06-賭けと軍配勝負.md のとおりテキサスホールデムで解決する。
 * したがって timing: 'battle' のカードは、
 *   - formationMod … 役（陣形）の段階を上げ下げする
 *   - holeCardMod  … 手勢札の枚数を増減する
 * のいずれかで効かせる。「サイコロ+1」のような旧仕様の効果は書かない。
 *
 * scope の読み方（core/effects.ts の Effect）:
 *   'attack' = 攻め手の役に作用  /  'defend' = 受け手の役に作用  /  'both' = 使用者の役に作用
 * したがって自分の役を上げたい攻め手は scope:'attack' の +1 を、
 * 相手（受け手）の役を崩したい攻め手は scope:'defend' の -1 を使うことになる。
 *
 * 【執筆基準】docs/05-学習設計.md §4 と provinces/_reference.ts に揃える。
 *   - 武将を「英雄」「悪」と評価しない。戦闘行為を美化・賛美しない
 *   - 宗教勢力（一向一揆・キリスト教・比叡山）は事実の記述に留める
 *   - 女性・民衆の存在を消さない。兵糧・輸送・籠城といった生活面の事実を必ず入れる
 *   - 通説と異説がある事項は caveat に必ず書く
 *   - ゲームの都合で史実を改変した箇所は gameNote に必ず書く
 *   - 年号は西暦を主とし、必要に応じて和暦を添える
 *
 * 【鉄砲関連の解禁】
 * 鉄砲・火薬・大砲にかかわるカードは availableFrom を 2 以降にしてある。
 * 1543年の鉄砲伝来より前に鉄砲札が山札に存在しないこと（docs/06 §2）と
 * 揃えるためで、これ自体が学習設計の一部である。
 *
 * 【この20枚の内訳】
 *   動員・兵站（4）  陣触・小荷駄隊・強行軍・狼煙
 *   移動・普請（2）  水軍・付城
 *   城攻め（3）      兵糧攻め・水攻め・玉薬
 *   合戦・攻め（4）  奇襲・夜襲・騎馬衆・鉄砲隊
 *   合戦・守り（4）  籠城・総構え・馬防柵・僧兵
 *   合戦・大筒（1）  大筒
 *   退却・救援（2）  後詰・殿
 */

import type { Card } from '../types';

// ---------------------------------------------------------------------------
// 動員と兵站
// ---------------------------------------------------------------------------

/** 出陣の触れを出して兵を集める。もっとも基本的な軍事カード。 */
export const JINBURE: Card = {
  id: 'card:gunji:jinbure',
  name: '陣触',
  reading: 'じんぶれ',
  type: 'gunji',
  cost: { zeni: 2 },
  timing: 'council',
  effects: [{ t: 'gain', res: 'hei', n: 4 }],
  note: {
    summary:
      '大名が家臣に出陣を命じる触れ。戦国期の軍勢の多くは、ふだん田畑を耕す人びとが動員されたものだった。',
    detail: {
      1: '「いくさに出るぞ」と家来たちに知らせることを陣触といいます。あつまるのは、ふだんは田や畑ではたらいている人たちが多く、田植えやいねかりの時期には人があつまりにくいので、いくさの時期もそれにあわせて決められました。',
      2: '大名が家臣に出陣を命じる通達を陣触という。戦国期の大名は常備軍を持たず、家臣とその配下の地侍・百姓を動員して軍勢を編成した。北条氏の着到状のように、知行の高に応じて「馬上何騎・鑓何本・旗何本」と負担が定められた例が残る。動員される人びとの多くは農業に従事していたため、田植えや稲刈りの時期は大規模な出陣がしにくく、合戦の時期は農事暦に強く縛られていた。',
      3: '陣触は大名が家臣団に対して出す出陣命令の通達で、これを受けた家臣は定められた人数・装備を率いて指定の場所に参集した。後北条氏の着到状は、知行役として馬上・鑓・弓・鉄砲・旗などの具体的な員数を書き上げており、軍役が知行高に応じて数量化されていたことを示す代表的な史料である。動員の実体は、家臣とその被官、さらに在地の地侍や百姓によって構成され、戦闘要員だけでなく夫丸（ぶまる）と呼ばれる輸送・普請の人夫も含まれた。この構造ゆえに、農繁期の大規模動員は困難であり、遠国への長期遠征も維持しにくかった。織田政権期以降に進んだとされる兵農分離は、この制約を外す方向の変化として理解されることが多いが、その進行度や画期については研究上の議論があり、地域差も大きい。動員される人びとの側から見れば、陣触は労働力の供出と負担の通知でもあった。',
    },
    keywords: ['陣触', '軍役', '着到状', '動員', '夫丸', '兵農分離'],
    caveat:
      '「兵農分離」がいつどこまで進んだかは研究上の議論があり、戦国期の軍勢を単純に「農民の兵」と言い切ることも、逆に「専業の武士だけ」と言い切ることもできません。地域と時期によって実態は大きく異なります。',
    gameNote:
      'ゲームでは銭を払えば即座に兵が増えますが、実際の動員には触れを出してから参集するまでの日数がかかり、農繁期には人が集まりませんでした。この時間差は遊びやすさのために省いています。',
  },
};

/** 兵糧と武具を運ぶ輸送部隊。合戦の記録に残りにくいが、軍勢を支えた実体。 */
export const KONIDA: Card = {
  id: 'card:gunji:konida',
  name: '小荷駄隊',
  reading: 'こにだたい',
  type: 'gunji',
  cost: { zeni: 1 },
  timing: 'council',
  effects: [{ t: 'gain', res: 'koku', n: 2 }],
  note: {
    summary:
      '兵糧・矢玉・武具を馬や人の背で運ぶ輸送部隊。軍勢の大きさは、戦える人数ではなく運べる兵糧の量で決まった。',
    detail: {
      1: '小荷駄隊（こにだたい）は、いくさに行く人たちの食べ物や道具を運ぶ係です。運べる食べ物の量よりも多くの人をつれていくことはできないので、いくさの大きさは、じつは運ぶ力で決まりました。運んだのは、村からあつめられた人びとでした。',
      2: '兵糧・矢玉・武具・炊事道具などを駄馬や人の背で運搬する部隊を小荷駄という。戦国期の軍勢は自前で食料を運ぶか現地で調達するしかなく、運搬能力が遠征の日数と規模の上限を決めた。運搬を担ったのは夫丸（ぶまる）と呼ばれる動員された百姓で、戦闘員ではないが軍勢の中で大きな割合を占めた。小荷駄を断たれた軍勢は退却を余儀なくされるため、輸送路の確保そのものが戦の勝敗を左右した。',
      3: '小荷駄（こにだ）は兵糧・馬糧・矢玉・武具・鍋釜などを輸送する部隊で、駄馬と人夫によって編成された。戦国期の遠征では、一人一日あたりの兵糧をおよそ六合前後と見積もる記述が軍学書に見えるが、これは近世に整理された数値で、実際の消費や支給の実態は地域と時期で異なる。輸送を担った夫丸は在地から軍役として徴発された百姓で、戦闘には加わらないが軍勢の人数には数えられた。したがって史料に見える「何万の軍勢」には、非戦闘の輸送要員が相当数含まれる。補給が続かない場合、軍勢は現地で食料を確保することになり、その過程で行われた刈田や乱取り（略奪・人の連行）は、戦場周辺の村々に深刻な被害をもたらした。合戦の記録は武将の動きを中心に書かれるが、その背後には食料を運び、田畑を荒らされ、時に連れ去られた人びとの生活があった。城攻めにおける兵糧攻めが有効だったのも、この輸送の脆さの裏返しである。',
    },
    keywords: ['小荷駄', '兵糧', '夫丸', '輸送', '補給', '軍役'],
    caveat:
      '一人一日あたりの兵糧を「六合」とする数値は近世の軍学書に整理されたもので、戦国当時の実際の支給量をそのまま示すものではありません。',
    gameNote:
      'ゲームでは小荷駄隊が石高を増やす形にしていますが、実際の小荷駄は食料を生み出すのではなく運ぶだけの存在です。「補給が続けば戦い続けられる」という関係を、資源の増加に置き換えています。',
  },
};

/** 兵糧の消耗と引き換えに距離を稼ぐ。行軍そのものを表す。 */
export const KYOUKOUGUN: Card = {
  id: 'card:gunji:kyoukougun',
  name: '強行軍',
  reading: 'きょうこうぐん',
  type: 'gunji',
  cost: { gunryaku: 1 },
  timing: 'anytime',
  effects: [
    { t: 'move', n: 2 },
    { t: 'lose', res: 'koku', n: 1 },
  ],
  note: {
    summary:
      '休息を削って軍勢を急がせること。戦国の行軍は一日およそ二十〜四十キロが目安で、それを超える移動は兵と馬を消耗させた。',
    detail: {
      1: '軍勢を休ませずに急いで進ませることを強行軍といいます。一日に歩ける道のりは決まっていて、むりに急ぐと人も馬もつかれ、食べ物もたりなくなります。それでも早く着いた方が有利なので、危険をしょうちで急ぐことがありました。',
      2: '軍勢を通常より速く長距離移動させることを強行軍という。戦国期の軍勢は小荷駄をともなうため、一日の行程はおよそ二十〜四十キロ程度が目安とされる。無理に急げば落伍者が出て隊列が伸び、到着時の戦闘力は落ちる。それでも急ぐ意味があったのは、敵が態勢を整える前に到達できるかどうかが勝敗を分けたからである。1582年に羽柴秀吉が備中から京へ引き返した行軍は「中国大返し」と呼ばれ、速さが戦局を変えた例として知られる。',
      3: '強行軍は、休息と隊列の整斉を犠牲にして移動速度を上げる行動を指す。戦国期の軍勢は小荷駄を伴い、また道路は近世の街道整備以前であったため、一日の行程は概ね六里から十里（およそ二十〜四十キロ）程度と見積もられることが多い。強行軍を行えば落伍と疲労が生じ、到着後の戦闘力は明らかに低下する。それでも実行されたのは、戦国期の戦役が「先に態勢を整えた側が有利」という構造を持っていたためである。有名な例として、1582年（天正10年）の本能寺の変の後、羽柴秀吉軍が備中高松から山崎まで引き返した「中国大返し」がある。ただしこの行軍の日程・距離・部隊の到着順については史料による差が大きく、後世の記録が速さを強調している面も指摘されている。行軍速度をめぐる議論は、当時の道路・渡河点・宿の収容力といった条件をどう見積もるかに左右され、確定した数値はない。なお、軍勢の移動は沿道の村々に宿と食料の負担を強い、通過そのものが在地にとって重い出来事だった。',
    },
    keywords: ['強行軍', '行軍', '中国大返し', '小荷駄', '街道'],
    caveat:
      '「中国大返し」の日数・距離・到着の順序は史料によって差があり、後世の記録が速さを強調している可能性が指摘されています。当時の一日の行程についても確定した数値はありません。',
    gameNote:
      '盤上では2マス進んで石高を1失う形にしていますが、実際の強行軍の代償は兵の疲労と落伍であり、石高が減るわけではありません。消耗を資源の減少に置き換えた表現です。',
  },
};

/** 情報の伝達。狼煙・早馬・飛脚といった通信を1枚にまとめてある。 */
export const NOROSHI: Card = {
  id: 'card:gunji:noroshi',
  name: '狼煙',
  reading: 'のろし',
  type: 'gunji',
  cost: { gunryaku: 1 },
  timing: 'anytime',
  effects: [{ t: 'draw', n: 2 }],
  note: {
    summary:
      '山上で煙や火を上げて遠方へ合図を送る仕組み。国境の見張りと結びつき、敵の来襲を短時間で本城へ伝えた。',
    detail: {
      1: '山の上で煙をあげて、遠くの味方に知らせるやり方を狼煙といいます。山から山へ順につないでいくと、馬で走るよりずっと早く知らせがとどきます。ただし伝えられるのは「敵が来た」くらいの短い合図だけでした。',
      2: '山頂などに設けた狼煙台で煙や火を上げ、次の台へ順に中継して遠方に合図を送る通信手段。古代には烽（とぶひ）と呼ばれ、戦国期には国境の見張りと結びついて用いられた。武田氏や後北条氏の領国には狼煙台と伝えられる地点が多く残る。馬による伝令よりはるかに速いが、伝えられる情報は「敵が来た」程度の単純な合図に限られ、詳しい内容は早馬や書状で別に送る必要があった。',
      3: '狼煙は、山頂等の高所に設けた施設で煙や火を上げ、視通の効く次の地点へ順次中継することで情報を遠距離へ伝達する仕組みである。古代律令制下では烽（とぶひ）として制度化され、中世・戦国期にも国境警備や城郭網と結びついて用いられた。武田氏の領国や後北条氏の領国には「狼煙台」「烽火台」と伝承される地点が数多く比定されているが、その多くは近世以降の地誌や近代以降の踏査によるもので、同時代史料で運用が確認できる例は限られる。伝達できる情報量は極めて少なく、事前に取り決めた符牒以上のことは送れないため、実務上は早馬・飛脚・書状と組み合わせて用いられた。戦国大名の領国支配においては、情報が届く速さそのものが軍事力の一部であり、伝馬制度の整備や宿の指定も同じ目的を持っていた。近世の宿駅制度は、こうした戦国期の仕組みを土台に整えられていく。',
    },
    keywords: ['狼煙', '烽', '伝馬', '情報伝達', '国境', '城郭網'],
    caveat:
      '各地に残る「狼煙台跡」の多くは近世以降の伝承や近代の踏査による比定で、戦国当時に実際どう運用されていたかを同時代史料で確認できる例は限られています。',
    gameNote:
      'ゲームでは「情報が早く届く」ことを手札の補充に置き換えています。実際の狼煙で送れたのは短い合図だけで、カードのように内容のある情報が届くわけではありません。',
  },
};

// ---------------------------------------------------------------------------
// 移動と普請
// ---------------------------------------------------------------------------

/** 海と川の道。瀬戸内・伊勢湾・熊野灘の海の勢力を1枚にまとめてある。 */
export const SUIGUN: Card = {
  id: 'card:gunji:suigun',
  name: '水軍',
  reading: 'すいぐん',
  type: 'gunji',
  cost: { zeni: 2 },
  timing: 'anytime',
  effects: [{ t: 'move', n: 3 }],
  note: {
    summary:
      '船を用いて海上を行動した勢力。輸送・警固・通行料の徴収を担い、大名に従うこともあれば独自に動くこともあった。',
    detail: {
      1: '船をつかって海の上ではたらいた人たちを水軍といいます。荷物を運んだり、船を守ったり、通る船からお金をとったりしました。瀬戸内海の村上氏がよく知られています。陸を歩くより船の方がずっと速く、たくさん運べました。',
      2: '船を用いて海上で活動した武士団を水軍（海賊衆）という。瀬戸内海の村上氏（能島・来島・因島）、志摩の九鬼氏、伊勢湾や熊野の勢力などが知られる。彼らは航路の警固を請け負って通行料（帆別銭）を取り、大名の求めに応じて輸送や海戦を担った。陸路が未整備な時代、船は人と物を最も速く大量に運ぶ手段であり、湊を押さえることは大名の経済力に直結した。特定の大名に完全に従属していたわけではなく、状況に応じて相手を変えることもあった。',
      3: '水軍あるいは海賊衆と呼ばれる勢力は、海上交通の要衝に拠って航行の警固・水先案内・輸送を請け負い、その対価として帆別銭などの通行料を徴収した海上武士団である。瀬戸内海の村上氏は能島・来島・因島の三家に分かれ、大内・毛利・河野といった周辺勢力と結びつきながらも独自の行動をとった。志摩の九鬼氏は織田政権に属し、1576年および1578年の木津川口における毛利方水軍との戦いに関わったことが知られる。1588年に豊臣政権が出した海賊停止令は、こうした自立的な海上勢力の通行料徴収を禁じ、大名の統制下に置く方向に働いた。当時の海運は年貢米・材木・塩・鉄・大陸からの輸入品を運ぶ動脈であり、船を扱ったのは武士だけでなく、廻船人・水主（かこ）・湊町の商人や女性の商人を含む広い層だった。「海賊」という語は略奪者の意味だけで理解されがちだが、当時の用法では海の領主権を持つ者を指す面が強く、単純な善悪では捉えられない。',
    },
    keywords: ['水軍', '海賊衆', '村上氏', '九鬼氏', '帆別銭', '海賊停止令'],
    caveat:
      '「海賊」という語は現代語の略奪者の語感で受け取られがちですが、当時は海上の通行を管理し警固料を取る領主的な存在を指す用法が強く、善悪で割り切れる区分ではありません。',
    gameNote:
      '盤上では3マス進む効果にしていますが、環状40マスの盤面は必ずしも海路を再現していません。海の移動が陸より速かったことを、単純な移動力に置き換えた表現です。',
  },
};

/** 城攻めのために築く臨時の城。長期の包囲を可能にした。 */
export const TSUKEJIRO: Card = {
  id: 'card:gunji:tsukejiro',
  name: '付城',
  reading: 'つけじろ',
  type: 'gunji',
  cost: { zeni: 2 },
  timing: 'anytime',
  effects: [{ t: 'build', level: 'shijou' }],
  note: {
    summary:
      '敵城を囲むために周囲へ築く臨時の城。付城を連ねて包囲網を作ることで、力攻めをせずに城を孤立させた。',
    detail: {
      1: '敵のお城をせめるとき、そのまわりに自分たちの小さなお城をいくつも作りました。これを付城（つけじろ）といいます。むりに攻めこむと味方も多く死ぬので、まわりを囲んで食べ物が入らないようにして、相手が出てくるのを待ったのです。',
      2: '攻城側が敵城の周囲に築く臨時の城砦を付城（つけじろ）または陣城という。土塁と堀、簡単な柵で構成され、包囲の拠点と補給路の遮断を兼ねた。付城を複数連ねて城を孤立させれば、正面から攻める必要がなくなる。羽柴秀吉による三木城攻め（1578〜1580年）や鳥取城攻め（1581年）では、付城と柵で城を囲い込む方法がとられた。城攻めは力攻めよりも包囲と兵糧の遮断が主流であり、その土木工事を担ったのは動員された人夫たちだった。',
      3: '付城（つけじろ）は攻城戦に際して包囲側が築く臨時の城砦で、陣城・向城とも呼ばれる。土塁・空堀・柵・櫓といった簡易な構造を持ち、包囲部隊の宿営、監視、補給線の確保、そして敵の後詰（救援軍）に備える役割を兼ねた。付城を相互に連結して包囲線を形成すれば、力攻めによる損害を避けつつ城を孤立させられる。羽柴秀吉の三木城攻め（1578〜1580年）では多数の付城と土塁・柵による封鎖線が構築され、鳥取城攻め（1581年）でも同様の方法がとられた。これらの遺構は発掘調査によって配置が確認されている例があり、当時の攻城の実像を知る手がかりになっている。付城の築造は大規模な土木工事であり、動員された夫丸や在地の百姓がその労働を担った。また包囲が長期化すれば、城内の武士だけでなく、城に逃げ込んだ周辺の住民が飢餓に直面することになった。攻城戦を「城と城の戦い」としてだけ描くと、この部分が見えなくなる。',
    },
    keywords: ['付城', '陣城', '三木城', '鳥取城', '包囲', '土木'],
    gameNote:
      '盤上では支城を建てる効果にしていますが、実際の付城は攻める側が敵地に築く一時的な施設で、戦が終われば放棄されるのが普通でした。ゲームでは築城の仕組みを共用しています。',
  },
};

// ---------------------------------------------------------------------------
// 城攻め
// ---------------------------------------------------------------------------

/** 包囲して兵糧を断つ。城攻めの主流の方法だった。 */
export const HYOUROUZEME: Card = {
  id: 'card:gunji:hyourouzeme',
  name: '兵糧攻め',
  reading: 'ひょうろうぜめ',
  type: 'gunji',
  cost: { zeni: 3 },
  timing: 'council',
  effects: [
    {
      t: 'target',
      who: 'chosen',
      then: [
        { t: 'lose', res: 'koku', n: 2 },
        { t: 'lose', res: 'hei', n: 2 },
      ],
    },
  ],
  note: {
    summary:
      '城を包囲して食料の搬入を断つ攻め方。損害を抑えられる一方、城内に逃げ込んだ住民を含む多数の餓死者を出した。',
    detail: {
      1: 'お城を囲んで、食べ物が中に入らないようにする戦い方です。むりに攻めるより味方は死にませんが、城の中では武士だけでなく、にげこんだ村の人たちも食べ物がなくなって、たくさんの人が命を落としました。',
      2: '城を包囲し、兵糧の搬入路を断って降伏に追い込む攻城法。力攻めに比べて攻城側の損害が小さく、戦国期の城攻めではこちらが主流だった。羽柴秀吉による三木城攻め（1578〜1580年）は「三木の干殺し」、鳥取城攻め（1581年）は「鳥取の渇え殺し」と後に呼ばれた。城には周辺の住民が避難していることが多く、包囲が長引くと武士より先に非戦闘の人びとが飢えた。鳥取城では開城にあたり城将の吉川経家が自害している。',
      3: '兵糧攻めは、城を包囲して補給路を遮断し、城内の食料が尽きるのを待つ攻城法である。攻城側の損害が小さく、また当時の城が長期の籠城を前提に大量の兵糧を備蓄していたわけではないため、戦国期の攻城戦ではむしろ標準的な方法だった。羽柴秀吉が別所長治の籠る三木城を攻めた1578〜1580年の戦い、および吉川経家の籠る鳥取城を攻めた1581年の戦いはその代表例で、いずれも付城と封鎖線によって出入りを断ち、さらに周辺で米を買い占めて城方が調達できないようにしたと伝えられる。後者は「鳥取の渇え殺し」と称され、宣教師ルイス・フロイスの記録を含む複数の史料が城内の惨状を伝えているが、記述には誇張が含まれる可能性も指摘される。重要なのは、こうした包囲下で最初に飢えたのが、戦闘に加わらない女性・子ども・老人、そして避難してきた周辺の村人だったという点である。開城の条件として城将が自害し、その代わりに城兵と住民の助命が図られる例が見られるのも、この構造と無関係ではない。兵糧攻めを「巧みな戦術」としてのみ語ると、その帰結が見えなくなる。',
    },
    year: [1578, 1581],
    keywords: ['兵糧攻め', '三木城', '鳥取城', '包囲', '飢餓', '開城'],
    caveat:
      '「三木の干殺し」「鳥取の渇え殺し」という呼び方は後世に広まったもので、史料が伝える城内の惨状の描写にも誇張が含まれる可能性が指摘されています。餓死者の数を示す確かな数字はありません。',
    gameNote:
      'ゲームでは相手の石高と兵を減らす効果にしていますが、実際の兵糧攻めは数か月から一年以上に及ぶ長期戦で、1手番で決着するものではありません。また盤上では飢えたのが誰かが表示されませんが、実際には城に逃げ込んだ住民が最も大きな被害を受けました。',
  },
};

/** 堤を築いて水を引き入れる。備中高松城の例で知られる。 */
export const MIZUZEME: Card = {
  id: 'card:gunji:mizuzeme',
  name: '水攻め',
  reading: 'みずぜめ',
  type: 'gunji',
  cost: { zeni: 4 },
  timing: 'council',
  effects: [
    {
      t: 'target',
      who: 'chosen',
      then: [
        { t: 'lose', res: 'koku', n: 3 },
        { t: 'lose', res: 'hei', n: 1 },
      ],
    },
  ],
  note: {
    summary:
      '低地の城の周囲に堤を築き、川の水を引き入れて孤立させる攻め方。1582年の備中高松城の例がよく知られる。',
    detail: {
      1: 'ひくい土地にあるお城のまわりに土手を作り、川の水を流しこんで、お城を水にうかんだ島のようにしてしまう戦い方です。1582年、羽柴秀吉が備中高松城でこの方法をつかいました。まわりの田や村も水につかってしまいました。',
      2: '低湿地にある城の周囲に長大な堤を築き、河川の水を導いて城を水没・孤立させる攻城法。1582年（天正10年）、羽柴秀吉が毛利方の清水宗治が守る備中高松城を攻めた際に用いた例が有名である。折しも本能寺の変が起きたため、秀吉は清水宗治の切腹を条件に毛利氏と和睦し、京へ引き返した。水攻めは築堤に膨大な人手と費用を要し、実行できる例は限られた。また水は城だけでなく周囲の田畑や村も浸し、その年の収穫が失われた。',
      3: '水攻めは、低湿地に立地する城の周囲に堤を築き、河川を堰き止めて水を導くことで城を孤立させる攻城法である。1582年（天正10年）の備中高松城攻めにおいて羽柴秀吉が実施した例が最もよく知られ、足守川の水を利用したとされる。堤の規模については、江戸期の記録や近代以降の推定で全長約三キロメートル前後とする説が広く流布しているが、同時代史料からその全長・高さを確定することはできず、実際にはより小規模な堰き止めであった可能性を含めて議論がある。この攻城戦の途中で本能寺の変が起き、秀吉は城将清水宗治の自刃を条件に毛利氏と講和して撤収した（後の「中国大返し」）。水攻めの実施には短期間で堤を築く大量の労働力と資材、そして地形条件が必要で、どの城にも使える方法ではなかった。また、堰き止められた水は城郭のみならず周辺の耕地・集落をも冠水させ、その年の年貢の基礎となる収穫を失わせた。攻城の技術として語られることが多いが、在地の生活基盤への打撃という側面を併せて見る必要がある。',
    },
    year: 1582,
    keywords: ['水攻め', '備中高松城', '清水宗治', '築堤', '足守川', '本能寺の変'],
    caveat:
      '備中高松城の水攻めの堤について、全長約三キロメートルという数値がよく紹介されますが、これは後世の記録や推定にもとづくもので、同時代史料から規模を確定することはできません。より小規模だったとする見方もあります。',
    gameNote:
      'ゲームでは銭を払えばどの相手にも使えますが、実際の水攻めは低湿地にある城でしか成立せず、堤を築く地形と人手が揃う特別な条件が必要でした。',
  },
};

/** 火薬の主原料である硝石。国内でほとんど採れず、輸入に頼った。 */
export const TAMAGUSURI: Card = {
  id: 'card:gunji:tamagusuri',
  name: '玉薬',
  reading: 'たまぐすり',
  type: 'gunji',
  cost: { zeni: 3 },
  timing: 'council',
  effects: [{ t: 'gain', res: 'gunryaku', n: 2 }],
  availableFrom: 2,
  note: {
    summary:
      '鉄砲に用いる火薬。主原料の硝石は国内でほとんど産出せず、南蛮貿易などによる輸入に頼っていた。',
    detail: {
      1: '鉄砲をうつには火薬がいります。火薬をつくるには硝石という材料がひつようですが、日本ではほとんどとれないので、外国から船で買っていました。だから鉄砲を持っていても、火薬がなければうてなかったのです。',
      2: '鉄砲や大筒に用いる火薬を玉薬（たまぐすり）、焔硝（えんしょう）などと呼ぶ。黒色火薬は硝石・硫黄・木炭を混ぜて作るが、硫黄と木炭は国内で得られるのに対し、主原料の硝石は日本でほとんど産出しなかった。そのため戦国期の硝石は中国や東南アジアからの輸入に大きく依存し、堺や博多の商人、南蛮貿易を通じて入手された。鉄砲を揃えるだけでなく、火薬を継続して確保できるかどうかが、鉄砲を実戦で使えるかを左右した。',
      3: '玉薬（焔硝・塩硝とも）は黒色火薬を指し、硝石（硝酸カリウム）・硫黄・木炭を配合して製する。硫黄は火山国である日本に豊富で、木炭も容易に得られたが、硝石は天然の産出がほとんどなく、これが火薬供給上の最大の制約となった。16世紀の日本は硝石の多くを中国・東南アジア方面からの輸入に依存し、堺・博多などの湊町の商人や、ポルトガル船を介した南蛮貿易がその流通を担った。硝石を扱える商人と結ぶことが、鉄砲を軍事力として運用する前提だったといえる。国内生産としては、古土法（家屋の床下などの土から硝酸塩を採取・培養する方法）や、のちの五箇山・白川郷などで行われた培養法が知られるが、これらが本格化した時期については議論があり、戦国末期に始まったとする見方と、近世に入って普及したとする見方がある。いずれにせよ戦国期の産出量は限られていた。鉄砲伝来がしばしば「一挺の値段」で語られるのに対し、実際に軍を動かすうえで効いたのは、火薬と鉛玉を継続的に調達できる流通の掌握だった。',
    },
    year: [1543, 1600],
    keywords: ['玉薬', '焔硝', '硝石', '南蛮貿易', '堺', '古土法'],
    caveat:
      '古土法などによる硝石の国内生産がいつ始まり、どの程度の量を賄えたかについては史料が乏しく、戦国末期に始まったとする説と近世に普及したとする説があります。',
    gameNote:
      'ゲームでは軍略が増える効果にしていますが、実際の玉薬は鉄砲を撃つための消耗品です。「火薬を押さえていると戦い方の選択肢が増える」という関係を軍略に置き換えています。鉄砲伝来より前には手に入らないため、第II章からの解禁としています。',
  },
};

// ---------------------------------------------------------------------------
// 合戦 — 攻め
// ---------------------------------------------------------------------------

/** 桶狭間の戦いに由来する。役を1段階上げる、攻め手の基本札。 */
export const KISHUU: Card = {
  id: 'card:gunji:kishuu',
  name: '奇襲',
  reading: 'きしゅう',
  type: 'gunji',
  cost: { gunryaku: 1 },
  timing: 'battle',
  effects: [{ t: 'holeCardMod', n: 1, scope: 'attack' }],
  note: {
    summary:
      '相手が備えていない時と場所を突く攻め方。1560年の桶狭間の戦いが例として知られるが、その経過には有力な異説がある。',
    detail: {
      1: '相手が思ってもいないところを、いきなり攻めることを奇襲といいます。1560年、織田信長が今川義元をやぶった桶狭間の戦いが有名です。ただし、うしろから回りこんで不意をついたという話は、今の研究ではちがうかもしれないといわれています。',
      2: '相手の備えが整っていない時機や地点を突く攻撃を奇襲という。1560年（永禄3年）の桶狭間の戦いで、織田信長が今川義元の本陣を突いて義元を討ったことが代表例として語られてきた。ただし、迂回して背後から急襲したという通説は、近年の研究では史料的根拠が弱いとされ、『信長公記』の記述にもとづき正面から本陣に攻めかかったとする見方が有力になっている。少数が多数を破った例として語られるが、両軍の兵力差の数字自体も後世の記録に由来する部分が大きい。',
      3: '奇襲は、敵の警戒が及ばない時機・地点・方向から攻撃を加える戦法を指す。日本史における代表例として挙げられるのが1560年（永禄3年）5月19日の桶狭間の戦いで、織田信長が尾張に侵攻した今川義元の本陣を攻撃し、義元が討たれた。この戦いは長らく「信長が善照寺砦から迂回し、豪雨に乗じて桶狭間山の背後から奇襲した」という筋立てで語られてきたが、この理解は江戸期の小瀬甫庵『信長記』に由来する部分が大きい。信長の右筆であった太田牛一の『信長公記』は迂回攻撃を記しておらず、正面からの攻撃と読める記述をしている。藤本正行らの研究以降、正面攻撃説が有力視されるようになった。兵力についても、今川方四万五千・織田方二千といった数字は後世の記録によるもので、実数は確定していない。ただし、義元が討たれたことによって今川氏の勢力が大きく後退し、松平元康（のちの徳川家康）が自立する契機となった、という結果そのものは動かない。',
    },
    year: 1560,
    keywords: ['奇襲', '桶狭間の戦い', '今川義元', '織田信長', '信長公記'],
    caveat:
      '桶狭間の戦いを「迂回による背後からの奇襲」とする通説は、近年の研究では史料的根拠が弱いとされ、正面から本陣に攻めかかったとする説が有力です。両軍の兵力の数字も後世の記録に由来し、実数はわかっていません。',
    gameNote:
      'カード名は「奇襲」ですが、上のとおり桶狭間が奇襲だったかどうかには議論があります。ゲームでは「不意を突けば少数でも役が立つ」という体験を作るために役の段階を上げる効果にしており、特定の戦いの再現ではありません。',
  },
};

/** 夜間の攻撃。相手（受け手）の役を1段階下げる。 */
export const YASHUU: Card = {
  id: 'card:gunji:yashuu',
  name: '夜襲',
  reading: 'やしゅう',
  type: 'gunji',
  cost: { gunryaku: 1 },
  timing: 'battle',
  effects: [{ t: 'holeCardMod', n: -1, scope: 'defend' }],
  note: {
    summary:
      '夜間に仕掛ける攻撃。統制が難しく味方も混乱しやすいため実行例は多くない。1546年の河越城の戦いが例として語られる。',
    detail: {
      1: '夜のくらいうちに攻めることを夜襲といいます。相手はねているので不意をつけますが、くらくて味方どうしもぶつかってしまうため、じつはむずかしい戦い方でした。1546年の河越城の戦いが夜の戦いとして知られています。',
      2: '夜間に行う攻撃を夜襲という。暗闇は奇襲の効果を高める一方で、部隊の統制がきかず同士討ちの危険が大きいため、実行された例は多くない。1546年（天文15年）の河越城の戦いは、北条氏康が河越城を包囲していた山内上杉・扇谷上杉・古河公方の連合軍を破った戦いで、「河越夜戦」の名で夜襲の例として語られる。ただし、この戦いの詳しい経過は江戸時代以降の軍記物によって広まったもので、同時代の史料は乏しく、実態は明らかでない。',
      3: '夜襲は夜間に敵陣へ仕掛ける攻撃で、視界が利かないため奇襲効果は大きいが、同時に部隊の掌握が困難で同士討ちを招きやすく、当時の指揮通信手段のもとでは高い危険を伴った。したがって史料上、明確な夜間攻撃の例は多くない。よく取り上げられるのが1546年（天文15年）の河越城の戦い（河越夜戦）である。前年から河越城を包囲していた山内上杉憲政・扇谷上杉朝定・古河公方足利晴氏の連合軍に対し、後詰として出陣した北条氏康の軍が攻撃を仕掛け、上杉朝定が戦死して扇谷上杉氏は事実上滅び、関東における北条氏の優位が固まった。ただし、「八万対八千」といった兵力差や、夜陰に乗じた急襲という劇的な経過は、『関八州古戦録』など江戸期の軍記物によって形づくられたもので、同時代史料による裏づけは乏しい。研究者のなかには戦闘の規模を大幅に小さく見積もる立場や、通説的な合戦像そのものに疑問を呈する立場もある。結果としての勢力交代は確かでも、その過程の描写は慎重に扱う必要がある、という典型的な例といえる。',
    },
    year: 1546,
    keywords: ['夜襲', '河越城の戦い', '北条氏康', '上杉氏', '軍記物'],
    caveat:
      '河越城の戦い（河越夜戦）で語られる兵力差や夜襲の経過は、江戸時代以降の軍記物によって広まったもので、同時代史料による裏づけは乏しく、戦闘の規模や経過については異説があります。',
    gameNote:
      'ゲームでは相手の役を1段階下げる効果にしていますが、実際の夜襲は仕掛けた側も混乱する危険の大きい戦法で、一方的に有利になるものではありませんでした。',
  },
};

/** 騎馬の衆。手勢札を1枚増やす。武田騎馬隊の像には異説がある。 */
export const KIBASHUU: Card = {
  id: 'card:gunji:kibashuu',
  name: '騎馬衆',
  reading: 'きばしゅう',
  type: 'gunji',
  cost: { gunryaku: 1 },
  timing: 'battle',
  effects: [{ t: 'holeCardMod', n: 1 }],
  note: {
    summary:
      '馬に乗って戦った上級の家臣たち。ただし戦場では馬を下りて戦うことも多く、集団で突撃する「騎馬隊」の像には異説がある。',
    detail: {
      1: '馬に乗って戦うのは、身分の高い武士だけでした。日本の馬は今の競走馬より小さく、戦いのときは馬からおりて戦うことも多かったと言われています。「武田の騎馬隊」が有名ですが、そのすがたには研究者のあいだで議論があります。',
      2: '騎乗して戦う資格を持つのは知行の大きい上級家臣に限られ、軍役状にも「馬上何騎」と記された。日本の在来馬は体高およそ130センチ前後と小柄で、甲冑を着けた武者を乗せて長時間走らせるには限界があった。宣教師ルイス・フロイスは、日本人は馬を戦場への移動に使い、戦うときは下馬すると記している。武田氏の「騎馬隊」が集団で突撃したというイメージは根強いが、これを疑問視する研究があり、実態については議論が続いている。',
      3: '戦国期において騎乗して戦うことができたのは、軍役上「馬上」として把握される上級家臣層に限られ、その割合は軍勢全体から見れば小さい。日本の在来馬（木曽馬などに近い系統）は体高おおむね130センチ前後で、西洋の軍馬に比べて小柄であり、甲冑を着けた乗り手と馬具の重量を考えれば、長距離の集団突撃には適さなかったと考えられている。イエズス会宣教師ルイス・フロイスの『日欧文化比較』には、ヨーロッパ人が騎乗して戦うのに対し日本人は下馬して戦う、という趣旨の記述がある。武田氏の「騎馬隊」が長篠で織田・徳川方の鉄砲隊に突撃して壊滅した、という語りは、江戸期の軍記物および近代の講談・小説によって強化されたもので、鈴木眞哉らはこれに批判的な検討を加えている。一方で、騎馬武者の集団運用そのものを全面的に否定できるわけではなく、平坦地での追撃や機動に馬が用いられたことは確かである。「騎馬隊は存在しなかった」と断ずるのも、「集団突撃が主戦法だった」と断ずるのも、現在の研究状況からは行き過ぎである。',
    },
    keywords: ['騎馬', '馬上', '在来馬', '武田氏', '下馬戦闘', 'フロイス'],
    caveat:
      '「武田騎馬隊が集団で突撃した」という像は江戸時代以降の軍記物や近代の創作で強められたもので、下馬して戦うことが多かったとする説があります。ただし騎馬の集団運用を全面的に否定できるわけでもなく、研究上の議論が続いています。',
    gameNote:
      '手勢札を増やす効果は、docs/06 §5 のとおり①陣触れの時点で確定します。このカードも陣触れで宣言して使うものとし、札が配られた後に出して増やすことはできません。',
  },
};

/** 鉄砲を備えた部隊。鉄砲伝来（1543）以降に解禁される。 */
export const TEPPOUTAI: Card = {
  id: 'card:gunji:teppoutai',
  name: '鉄砲隊',
  reading: 'てっぽうたい',
  type: 'gunji',
  cost: { zeni: 3 },
  timing: 'battle',
  effects: [
    // docs/06 §4 の補正表は「長篠以降の鉄砲 +1（手勢札）」の一本。
    // 段階補正との二重計上にしない。
    { t: 'holeCardMod', n: 1 },
  ],
  availableFrom: 2,
  note: {
    summary:
      '火縄銃を装備した部隊。1543年に伝来した鉄砲は国内で量産され、十数年のうちに合戦の主要な武器となった。',
    detail: {
      1: '1543年、種子島に鉄砲がつたわりました。日本の職人はすぐに同じものを作れるようになり、堺や国友などで数多く作られました。鉄砲は高く、火薬も外国から買わなければならないので、お金のある大名ほどたくさん持てました。',
      2: '1543年（天文12年）、ポルトガル人を乗せた船が種子島に漂着し、鉄砲（火縄銃）が伝えられたとされる。国内の鍛冶はまもなく複製に成功し、堺・国友・根来などが生産地となった。命中精度と貫通力は高いが、装填に時間がかかり雨に弱いという弱点もあった。鉄砲そのものが高価なうえ、火薬の原料である硝石は輸入に頼らざるを得ず、鉄砲を数多く運用できるかどうかは、大名の財力と流通の掌握に直結していた。',
      3: '鉄砲（火縄銃）は1543年（天文12年）、種子島に漂着した船に乗っていたポルトガル人によって伝えられたとされる。この年次と経緯は、1606年に成立した『鉄炮記』を主要な典拠としており、1542年とする説や、それ以前に倭寇の交易ルートを通じて東アジア各地に火器が流通していたことを重視する見解もある。伝来後の国産化は速く、種子島に続いて紀伊根来、和泉堺、近江国友などが主要な生産地となった。火縄銃は有効射程・貫通力の点で弓を上回る場面がある一方、次弾装填に時間を要し、降雨時には火縄と火皿が機能しないという制約があった。したがって「鉄砲の登場が一挙に戦術を変えた」という単純な図式は成り立たず、弓・鑓・鉄砲を組み合わせた運用が実態に近い。むしろ決定的だったのは、鉄砲そのものの価格に加えて、火薬の主原料である硝石をほぼ輸入に頼らざるを得なかったことである。堺や博多の商人との結びつき、南蛮貿易への関与といった経済的条件が、鉄砲を軍事力に転化できるかを左右した。鉄砲が「金で買える強さ」であったことは、戦国後期の大名間の格差を広げる要因でもあった。',
    },
    year: 1543,
    keywords: ['鉄砲', '火縄銃', '種子島', '堺', '国友', '硝石'],
    caveat:
      '鉄砲伝来の年を1543年とするのは『鉄炮記』（1606年成立）にもとづくもので、1542年とする説もあります。また、それ以前から東アジアの交易を通じて火器が流入していた可能性も指摘されています。',
    gameNote:
      '手勢札を1枚増やす効果は、docs/06 §4 の補正表（長篠以降の鉄砲＝+1）に合わせたもので、docs/06 §5 のとおり①陣触れの時点で宣言して使います。また、鉄砲札は第II章まで山札に存在しないため、このカードも第II章からの解禁としています。銭のコストが重いのは、鉄砲と火薬が高価だったことを反映したものです。',
  },
};

// ---------------------------------------------------------------------------
// 合戦 — 守り
// ---------------------------------------------------------------------------

/** 城に拠って守る。受け手の役を1段階上げる。 */
export const ROUJOU: Card = {
  id: 'card:gunji:roujou',
  name: '籠城',
  reading: 'ろうじょう',
  type: 'gunji',
  cost: { zeni: 1 },
  timing: 'battle',
  effects: [{ t: 'holeCardMod', n: 1, scope: 'defend' }],
  note: {
    summary:
      '城に立てこもって守ること。城には武士だけでなく周辺の村人も避難し、女性を含む多くの人が炊事や普請に加わった。',
    detail: {
      1: 'お城に立てこもって守ることを籠城といいます。城の中には、戦う武士だけでなく、まわりの村からにげてきた人たちもいました。ごはんをたいたり、けが人の世話をしたり、土をはこんだりする仕事を、女の人や子どももしていました。',
      2: '城に立てこもって攻撃を防ぐことを籠城という。城は攻める側に大きな損害を強いるため、守る側は少ない兵でも持ちこたえられた。ただし籠城が成り立つのは、味方の後詰（救援軍）が来る見込みがある場合か、相手が包囲を続けられない事情がある場合に限られる。戦国期の城には周辺の村人が財産とともに避難する例が多く、城内では女性や子どもも炊事・看護・運搬・普請にあたった。籠城は武士だけの出来事ではなかった。',
      3: '籠城は、城郭に拠って攻撃を防ぐ戦法である。堀・土塁・虎口といった防御施設は攻城側に不利な条件を強いるため、守備側は寡兵でも相当期間持ちこたえることができた。ただし籠城それ自体は勝利をもたらさない。後詰（救援軍）の到着、包囲側の兵糧欠乏、あるいは政治的な状況の変化といった外部条件が伴って初めて意味を持つ。後詰の見込みがなければ、籠城は開城条件の交渉のための時間稼ぎとなる。戦国期の城郭は在地社会と密接に結びついており、合戦が近づくと周辺の百姓が家財・食料を持って城内や寺社に避難する「小屋懸け」の慣行が各地で確認される。城内では女性や子どもも炊事・傷病者の世話・土運び・矢の補給などに従事した。1590年の忍城の戦いのように、地域の住民を含む籠城が長期にわたった例も知られる。また開城の際には、城将の自刃と引き換えに城兵や住民の助命を図る交渉が行われることがあった。籠城戦を城主と攻城軍の対決としてのみ描くと、そこに含まれる大多数の人びとの経験が見えなくなる。',
    },
    keywords: ['籠城', '城郭', '後詰', '小屋懸け', '開城', '民衆'],
    gameNote:
      '盤上では銭1で役を1段階上げる軽いカードにしていますが、実際の籠城には十分な兵糧・水・弾薬の備蓄が前提で、それが尽きれば城は落ちました。備蓄の管理はゲームでは省略しています。',
  },
};

/** 城下町ごと囲い込む惣構。小田原城の例で知られる。受け手の手勢札を2枚増やす。 */
export const SOUGAMAE: Card = {
  id: 'card:gunji:sougamae',
  name: '総構え',
  reading: 'そうがまえ',
  type: 'gunji',
  cost: { zeni: 3 },
  timing: 'battle',
  // docs/06 §4「役を1段階上げる」は捨てた。防御 +2段階は勝率 94.4% で攻略不能に
  // なるため、天守と同じ「手勢札 +2」に置き換える（合計は 2〜4枚に丸められる）。
  effects: [{ t: 'holeCardMod', n: 2 }],
  availableFrom: 3,
  note: {
    summary:
      '城だけでなく城下町ごと堀と土塁で囲い込む構え。小田原城の総構えは全長およそ9キロに及んだと推定されている。',
    detail: {
      1: 'お城だけでなく、そのまわりの町ぜんぶを、堀と土のかべで囲んでしまうやり方を総構えといいます。小田原のお城の総構えはとても大きく、一周が9キロくらいあったと考えられています。町の人もいっしょに中に入って守りました。',
      2: '城郭本体だけでなく、城下町や家臣屋敷を含む一帯を堀と土塁で囲い込む構造を総構え（惣構）という。後北条氏の小田原城の総構えは全長およそ9キロメートルに及ぶと推定され、1590年の豊臣秀吉による小田原攻めの際にはこの内側に城下の住民も収容された。総構えは町と住民を城の内側に取り込むもので、守りの構造であると同時に、城下町を都市として囲い込む装置でもあった。大坂城や京都の御土居にも同じ発想が見られる。',
      3: '総構え（惣構）は、城郭の主要部のみならず、家臣屋敷・町屋・寺社を含む城下一帯を堀と土塁によって包囲する防御構造である。後北条氏の小田原城のものが規模の点で著名で、発掘調査と地形の検討から全長約9キロメートルに達したと推定されている。1590年（天正18年）の豊臣秀吉による小田原攻めに際しては、この内側に城下の住民が収容されたと伝えられる。総構えの意義は軍事面にとどまらない。城下町を物理的な境界で画することは、都市の範囲を確定し、住民の把握や商工業の掌握を進めることにもつながった。豊臣期の大坂城にも総構えが築かれ、京都では1591年に御土居と呼ばれる土塁と堀が市街を囲んだ。これらは戦国期の城郭が近世城下町へと転じていく過程を示す遺構でもある。なお、総構えを備えていても小田原城は開城しており、防御施設の規模が結果を決めたわけではない。籠城の帰趨は補給・後詰・外交状況によって決まる、という原則はここでも当てはまる。',
    },
    year: 1590,
    keywords: ['総構え', '惣構', '小田原城', '後北条氏', '御土居', '城下町'],
    caveat:
      '小田原城の総構えの全長「約9キロ」は発掘調査と地形からの推定値で、築造の時期についても一度に完成したのか段階的に拡張されたのか、議論があります。',
    gameNote:
      '盤上では受け手の手勢札を2枚増やす形にしています（docs/06 §4。合計は2〜4枚に丸められます）。手勢札を増やす効果は docs/06 §5 のとおり①陣触れの時点で宣言して使います。第III章からの解禁は安土城以降の築城技術の発達に合わせた区切りで、小田原の総構えが実際に攻められたのは1590年（第IV章）にあたります。',
  },
};

/** 長篠の設楽原に築かれた柵。攻め手の役を1段階下げる。 */
export const BABOUSAKU: Card = {
  id: 'card:gunji:babousaku',
  name: '馬防柵',
  reading: 'ばぼうさく',
  type: 'gunji',
  cost: { zeni: 1 },
  timing: 'battle',
  effects: [{ t: 'holeCardMod', n: -1, scope: 'attack' }],
  availableFrom: 3,
  note: {
    summary:
      '野戦の陣地に立てる柵。1575年の長篠の戦いで織田・徳川方が設楽原に築いたものが知られる。',
    detail: {
      1: '野原で戦うときに、木のくいをならべて柵をつくり、敵が近づけないようにしました。1575年の長篠の戦いで、織田信長と徳川家康がこの柵を作って武田軍とたたかいました。有名な「三段撃ち」という話は、たしかな記録がなく、あやしいと言われています。',
      2: '野戦において陣地の前面に立てる柵を馬防柵という。1575年（天正3年）の長篠の戦いでは、織田信長・徳川家康の連合軍が設楽原に柵と堀を設けて陣地を築き、武田勝頼の軍と戦った。武田方は多くの有力な家臣を失い、以後の武田氏は勢いを失った。ただし、鉄砲隊を三列に分けて交互に撃ったという「三段撃ち」は、当時の史料である『信長公記』には書かれておらず、後世の記述に由来するとされる。鉄砲の数についても史料により差がある。',
      3: '馬防柵は、野戦築城において陣地の前面に立てる柵で、騎馬の突入を阻み、また敵の接近を一定の線で止めることで飛道具の効果を高める役割を持つ。1575年（天正3年）の長篠の戦いにおいて、織田信長・徳川家康の連合軍は設楽原に堀と柵を伴う陣地を構築し、長篠城を包囲していた武田勝頼の軍と対戦した。武田方は山県昌景・馬場信春ら多くの重臣を失い、この敗北以降、武田氏の勢力は後退していく。この戦いをめぐっては、後世に形づくられた説明が多い。鉄砲隊を三隊に分けて順次射撃したとする「三段撃ち」は、太田牛一『信長公記』には見られず、小瀬甫庵『信長記』以降の記述に由来する。鉄砲の数も『信長公記』の写本により「三千挺」「千挺」と異同があり、確定できない。また、武田方の敗因を騎馬突撃と鉄砲の対決に還元する図式そのものが、近年の研究では見直されている。長篠城の後詰として不利な条件で決戦に応じた戦略判断、両軍の兵力差、陣地の構築といった要素が重視されるようになっている。とはいえ、野戦築城と飛道具を組み合わせる戦い方がこの時期に一般化していったこと自体は、複数の戦例から確認できる。',
    },
    year: 1575,
    keywords: ['馬防柵', '長篠の戦い', '設楽原', '野戦築城', '三段撃ち', '信長公記'],
    caveat:
      '長篠の戦いの「三段撃ち」は『信長公記』に記述がなく、後世の軍記物に由来するもので、史料的な裏づけが乏しいとされます。鉄砲の数も写本により三千挺・千挺と異同があり、確定していません。武田方の敗因を騎馬と鉄砲の対決に還元する説明も見直されています。',
    gameNote:
      '盤上では第III章からの解禁としています。これは1575年の長篠が第III章にあたるためで、柵そのものはそれ以前にも用いられていました。',
  },
};

/** 寺院に属した武装集団。事実の記述に留める。 */
export const SOUHEI: Card = {
  id: 'card:gunji:souhei',
  name: '僧兵',
  reading: 'そうへい',
  type: 'gunji',
  cost: { zeni: 2 },
  timing: 'battle',
  effects: [
    {
      t: 'choice',
      options: [
        {
          label: '加勢を請う（手勢札を1枚増やす）',
          then: [{ t: 'holeCardMod', n: 1 }],
        },
        {
          label: '寺社と和し、寄進を行う（威信を2得る）',
          then: [{ t: 'gain', res: 'ishin', n: 2 }],
        },
      ],
    },
  ],
  note: {
    summary:
      '延暦寺や興福寺などの大寺院に属し、武装して行動した集団。「僧兵」は近世以降の呼び名で、当時は大衆・衆徒などと呼ばれた。',
    detail: {
      1: '大きなお寺には、たくさんの人がくらしていて、そのなかには武器を持って行動する人たちもいました。今は「僧兵」と呼びますが、これは江戸時代からの言い方で、当時は「大衆（だいしゅ）」などと呼ばれていました。お寺は土地やお金も持っていて、大名と話し合ったり争ったりしました。',
      2: '延暦寺・興福寺・園城寺などの大寺院に属し、武装して寺の要求を通そうとした集団を、近世以降「僧兵」と呼ぶ。当時の史料では大衆（だいしゅ）・衆徒（しゅと）・堂衆・悪僧などと記される。大寺院は広大な荘園と多くの人を抱える経済的・社会的な組織であり、その権益をめぐって朝廷・幕府・大名と交渉し、時に武力を用いた。戦国期には各地の寺院が城郭のような構えを持ち、門前や寺内には商人・職人が住んで町を形づくっていた。',
      3: '「僧兵」は近世以降に一般化した呼称であり、平安から戦国にかけての史料では大衆（だいしゅ）・衆徒（しゅと）・堂衆・悪僧・法師武者などと表記される。延暦寺・興福寺・園城寺といった大寺院は、荘園領主として広大な所領を持ち、内部に学侶・堂衆・寄人といった階層を抱えた組織であった。武装して神輿を奉じ朝廷へ要求を突きつける強訴は、こうした組織が持つ交渉手段の一つである。戦国期に入ると、各地の寺院が堀や土塁を備えた寺内町を形成し、浄土真宗本願寺派のように門徒の共同体が広域の政治勢力となる例も現れた。加賀では1488年以降、門徒と国人が守護富樫政親を退けて長く自治的な支配が続いた。1571年には織田信長の軍が延暦寺を攻撃し、多数の犠牲を出している。これらの出来事は、しばしば「宗教勢力と武家の対立」という図式で語られるが、実際には寺院・門徒・町衆・国人・大名が入り組んだ利害のなかで動いており、一方を正しい、他方を誤っていると評価できる性質のものではない。寺院は同時に、学問・医療・葬送・金融・流通の担い手でもあった。',
    },
    keywords: ['僧兵', '大衆', '衆徒', '延暦寺', '寺内町', '荘園'],
    caveat:
      '「僧兵」という語は江戸時代以降に一般化した呼び方で、当時の史料では大衆・衆徒・悪僧などと記されます。また寺院勢力と大名の関係は対立だけでなく、寄進・保護・共存を含む多様なもので、単純な敵味方では捉えられません。',
    gameNote:
      '軍札の僧兵は第IV章の刀狩で13枚から7枚に減ります（docs/06 §2）。このカードは刀狩後も使えますが、山札に僧兵札が減るぶん、実際の効き目は弱まる想定です。手勢札を増やす選択肢は①陣触れで宣言して使います。',
  },
};

// ---------------------------------------------------------------------------
// 合戦 — 大筒
// ---------------------------------------------------------------------------

/** 大友氏が用いたと伝わる大砲。相手の手勢札を2枚減らす。 */
export const OODUTSU: Card = {
  id: 'card:gunji:oodutsu',
  name: '大筒',
  reading: 'おおづつ',
  type: 'gunji',
  cost: { zeni: 5 },
  timing: 'battle',
  // docs/06 §4 のとおり段階補正は使わない。相手の手勢札を減らす形で効かせる
  // （寝返りと同じ書き方。合計は 2〜4枚に丸められる）。
  effects: [{ t: 'target', who: 'chosen', then: [{ t: 'holeCardMod', n: -2 }] }],
  availableFrom: 3,
  note: {
    summary:
      '鉄砲より口径の大きい火器。大友宗麟がポルトガルから入手した大砲は「国崩」と呼ばれ、臼杵城の防戦に用いられたと伝わる。',
    detail: {
      1: '鉄砲よりずっと大きな筒で玉をうつ武器を大筒といいます。九州の大友宗麟は、ポルトガルから大きな大砲を手に入れ、「国崩（くにくずし）」という名をつけたと伝えられています。ただし、この時代の日本では大砲はとても数が少なく、めったに使われませんでした。',
      2: '鉄砲より口径の大きい火器を大筒といい、輸入された西洋式の後装砲はフランキ砲と呼ばれた。豊後の大友宗麟は1576年（天正4年）にポルトガル人からこの砲を入手したと伝えられ、「国崩（くにくずし）」と名づけたという。1586年（天正14年）に島津氏の軍が臼杵城に迫った際に用いられたと伝わる。ただし戦国期の日本で大砲が戦局を左右した例はごく限られ、火薬・鋳造技術・輸送のいずれの面でも普及の障壁が大きかった。',
      3: '大筒は鉄砲より口径の大きい火器の総称で、国内で鋳造・鍛造された大型の筒のほか、ヨーロッパから輸入された後装式の砲（フランキ砲、仏郎機砲）を含む。豊後の大友宗麟がポルトガル人を通じて入手したと伝えられる砲は「国崩」と称され、臼杵城跡に伝来品とされる砲が残る（現在同城跡にあるのはレプリカで、原品は別に保管されている）。入手の年を1576年（天正4年）とする記述が広く流布するが、伝来の経緯や年次は後世の記録に依るところが大きく、確定的とはいえない。1586年（天正14年）の島津氏による臼杵城攻撃の際に用いられたと伝わる。戦国期日本において大砲が普及しなかった理由は複数ある。第一に、火薬の主原料である硝石が輸入依存で、消費量の大きい大砲を継続運用しにくかったこと。第二に、大型の砲を鋳造する技術と設備が限られたこと。第三に、山がちで道路が未整備な地形では重量物の輸送が困難だったこと。第四に、日本の城郭が土塁と堀を主体としており、石造城壁を破壊するための攻城砲の必要性が相対的に低かったことである。大砲が本格的に用いられるのは、1614〜1615年の大坂の陣を待つことになる。',
    },
    year: 1576,
    keywords: ['大筒', 'フランキ砲', '国崩', '大友宗麟', '臼杵城', '南蛮貿易'],
    caveat:
      '「国崩」の入手年を1576年とする説明は広く流布していますが、伝来の経緯や年次は後世の記録に依るところが大きく、確定していません。臼杵城跡に展示されているのはレプリカです。',
    gameNote:
      '盤上では「相手の手勢札を2枚減らす」としています（docs/06 §4。合計は2〜4枚に丸められ、①陣触れの時点で宣言して使います）。実際の戦国期に大砲が戦局を左右した例はごくわずかで、これほど一般的な武器ではありませんでした。銭のコストを最も重くしているのはそのためです。',
  },
};

// ---------------------------------------------------------------------------
// 救援と退却
// ---------------------------------------------------------------------------

/** 籠城する味方を救うために向かう軍勢。合戦の進行の名でもある。 */
export const GOZUME: Card = {
  id: 'card:gunji:gozume',
  name: '後詰',
  reading: 'ごづめ',
  type: 'gunji',
  cost: { gunryaku: 1 },
  timing: 'reaction',
  effects: [{ t: 'gain', res: 'hei', n: 3 }],
  note: {
    summary:
      '包囲された味方の城を救うために向かう軍勢。後詰が来るかどうかが、籠城が成り立つかどうかを決めた。',
    detail: {
      1: '味方のお城が囲まれたとき、助けにいく軍勢を後詰（ごづめ）といいます。城に立てこもる人たちは、助けが来ると信じているから、がまんして守り続けられます。助けが来ないとわかると、城をわたして降りることが多かったのです。',
      2: '包囲された味方の城を救援するために出動する軍勢を後詰（ごづめ）という。籠城は単独では勝利につながらず、後詰の到着が見込めるかどうかが継続の可否を決めた。逆に、城を囲む側は後詰の襲来に備えて陣地を築く必要があり、しばしば「後詰決戦」と呼ばれる野戦が発生した。1575年の長篠の戦いも、長篠城を包囲する武田方に対し織田・徳川方が後詰として出陣したことから起きた戦いである。後詰は本作の合戦の進行の名前にも使われている。',
      3: '後詰は、包囲を受けた味方の城郭を救援するために派遣される部隊を指す語である。中世から戦国期の攻城戦の構造上、籠城側は単独で攻城側を撃退することが難しく、後詰の到来を前提として持ちこたえるのが基本形だった。したがって攻城側は、城の封鎖と並行して後詰への備えを固める必要があり、付城や陣地の構築はこの二重の目的を持つ。後詰が到着すれば野戦が生起し、これを後詰決戦と呼ぶ。1546年の河越城の戦いは上杉方に包囲された河越城への北条氏の後詰として、1575年の長篠の戦いは武田方に包囲された長篠城への織田・徳川方の後詰として起きたもので、いずれも城の帰属を超えて勢力関係を大きく変えた。逆に後詰が来ないと判断された城は、条件交渉のうえ開城することが多く、これは無用の犠牲を避ける現実的な選択でもあった。本作では合戦の第五段階（リバー）の名称としても「後詰」を用いており、遊びながら語が身につくようにしてある。',
    },
    keywords: ['後詰', '籠城', '後詰決戦', '付城', '長篠の戦い', '河越城'],
    gameNote:
      'ゲームでは兵を3得る効果にしていますが、実際の後詰は別動の軍勢が到着することであり、兵が湧いて出るわけではありません。合戦の進行における「後詰」（戦場札の5枚目を開く段階）とは別のものなので、混同しないようにしてください。',
  },
};

/** 退却の最後尾を務める部隊。金ヶ崎の退き口が例として語られる。 */
export const SHINGARI: Card = {
  id: 'card:gunji:shingari',
  name: '殿',
  reading: 'しんがり',
  type: 'gunji',
  cost: { gunryaku: 1 },
  timing: 'reaction',
  effects: [
    { t: 'gain', res: 'hei', n: 2 },
    { t: 'gain', res: 'ishin', n: 1 },
  ],
  note: {
    summary:
      '退却する軍勢の最後尾に留まり、追撃を食い止める役。危険が大きいため、務めた者は高く評価された。',
    detail: {
      1: 'いくさに負けて引きあげるとき、いちばん後ろに残って敵を食い止める役を殿といいます。にげおくれれば自分がやられてしまうので、とても危険な役でした。1570年、織田信長が越前から引きあげたときの話がよく知られています。',
      2: '退却する軍勢の最後尾に位置し、追撃してくる敵を食い止める部隊を殿（しんがり）という。退却は隊列が崩れやすく損害が拡大しやすいため、殿の働きが軍勢全体の生死を分けた。1570年（元亀元年）、越前の朝倉氏を攻めていた織田信長は、同盟していた浅井長政の離反により退路を断たれる危険に陥り、京へ撤退した。これが金ヶ崎の退き口と呼ばれる。木下秀吉が殿を務めたと語られるが、池田勝正や明智光秀も殿にあったとする史料があり、秀吉一人の功とする話は後世に強調された面がある。',
      3: '殿（しんがり）は、退却時に軍列の最後尾に留まり追撃を阻止する任務およびその部隊を指す。前近代の軍隊において、退却は最も損害が出やすい局面であり、隊形が崩れたところを追撃されれば壊走に転じる。したがって殿は高い練度と統率を要する危険な役目であり、これを果たした者は戦功として評価された。よく知られるのが1570年（元亀元年）の金ヶ崎の退き口である。織田信長は徳川家康らとともに越前の朝倉義景を攻めていたが、近江の浅井長政が離反したことで挟撃の危険が生じ、急遽撤退した。この撤退戦で木下秀吉（のちの豊臣秀吉）が殿を務めたという逸話は広く知られるが、同時代に近い史料からは池田勝正が殿の中心であったとする見方や、明智光秀も加わっていたとする指摘があり、秀吉単独の功績とする語りは後年に整えられた可能性が高い。なお撤退そのものは軍事的な失敗ではなく、状況判断としての撤退は当時から高く評価されていた。本作の合戦でも、退き陣（フォールド）によって国を渡してでも兵を残す判断が用意されている（docs/06 §5）。',
    },
    year: 1570,
    keywords: ['殿', '金ヶ崎の退き口', '撤退', '浅井長政', '朝倉氏', '木下秀吉'],
    caveat:
      '金ヶ崎の退き口で木下秀吉が殿を務めたという逸話は広く知られていますが、池田勝正が中心だったとする見方や明智光秀も加わっていたとする指摘があり、秀吉一人の功とする語りは後年に強調された可能性があります。',
    gameNote:
      'ゲームでは兵と威信を得る効果にしていますが、実際の殿は「失う兵を減らす」役目であって、兵が増えるわけではありません。損失の軽減を加算に置き換えた表現です。',
  },
};

// ---------------------------------------------------------------------------
// まとめ
// ---------------------------------------------------------------------------

/** 軍事カード 20枚。 */
export const GUNJI_CARDS: readonly Card[] = [
  // 動員と兵站
  JINBURE,
  KONIDA,
  KYOUKOUGUN,
  NOROSHI,
  // 移動と普請
  SUIGUN,
  TSUKEJIRO,
  // 城攻め
  HYOUROUZEME,
  MIZUZEME,
  TAMAGUSURI,
  // 合戦・攻め
  KISHUU,
  YASHUU,
  KIBASHUU,
  TEPPOUTAI,
  // 合戦・守り
  ROUJOU,
  SOUGAMAE,
  BABOUSAKU,
  SOUHEI,
  // 大筒
  OODUTSU,
  // 救援と退却
  GOZUME,
  SHINGARI,
];
